import { useMemo, useEffect } from 'react';

export const useScrollLightboxIntoView = (position, containerRef, options = {}) => {
  const {
    assumedHeight,
    estimatedWidth,
    bottomSpace = 50
  } = options;

  // Capture initial scroll position when hook is first initialized
  const initialScrollPosition = useMemo(() => ({
    scrollX: typeof window !== "undefined" ? window.scrollX : 0,
    scrollY: typeof window !== "undefined" ? window.scrollY : 0,
  }), []);

  // Calculate adjusted position based on initial scroll
  const adjustedLeft = (position?.x ?? 0) + initialScrollPosition.scrollX;
  const adjustedTop = (position?.y ?? 0) + initialScrollPosition.scrollY;

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !assumedHeight) return;

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Calculate lightbox position based on its absolute positioning
    const lightboxTop = adjustedTop;
    const lightboxLeft = adjustedLeft;
    
    // Estimate lightbox dimensions
    const lightboxWidth = estimatedWidth ?? Math.min(600, viewportWidth * 0.8);
    const lightboxHeight = assumedHeight + bottomSpace;
    
    // Calculate if lightbox would be fully visible
    const lightboxBottom = lightboxTop + lightboxHeight;
    const lightboxRight = lightboxLeft + lightboxWidth;
    
    const isFullyVisible = 
      lightboxTop >= window.scrollY && 
      lightboxLeft >= window.scrollX && 
      lightboxBottom <= window.scrollY + viewportHeight && 
      lightboxRight <= window.scrollX + viewportWidth;

    // Only scroll if not fully visible
    if (!isFullyVisible) {
      // Calculate the center position for the lightbox
      const centerX = lightboxLeft + lightboxWidth / 2;
      const centerY = lightboxTop + lightboxHeight / 2;
      
      // Calculate target scroll position to center the lightbox
      const targetScrollX = centerX - viewportWidth / 2;
      const targetScrollY = centerY - viewportHeight / 2;
      
      // Ensure we don't scroll beyond document bounds
      const maxScrollX = Math.max(0, document.documentElement.scrollWidth - viewportWidth);
      const maxScrollY = Math.max(0, document.documentElement.scrollHeight - viewportHeight);
      
      const finalScrollX = Math.max(0, Math.min(targetScrollX, maxScrollX));
      const finalScrollY = Math.max(0, Math.min(targetScrollY, maxScrollY));
      
      // Smooth scroll to center the lightbox
      window.scrollTo({
        left: finalScrollX,
        top: finalScrollY,
        behavior: 'smooth'
      });
    }
  }, [adjustedLeft, adjustedTop, assumedHeight, estimatedWidth, bottomSpace]);

  return {
    adjustedLeft,
    adjustedTop,
    initialScrollPosition
  };
};
